import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ChevronRight, ChevronLeft, Loader2, ShieldCheck, AlertTriangle,
  CheckCircle2, FileText, GraduationCap, Briefcase, Wallet, BookOpen, MessageSquare, User, Mail } from "lucide-react";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const STORAGE_KEY = "studynet_gs_builder_v1";

const STEP_DEFS = [
  { id: 1, label: "Personal", icon: User },
  { id: 2, label: "Education", icon: GraduationCap },
  { id: 3, label: "Employment", icon: Briefcase },
  { id: 4, label: "Financial", icon: Wallet },
  { id: 5, label: "Course", icon: BookOpen },
  { id: 6, label: "GS Q&A", icon: MessageSquare },
  { id: 7, label: "Review", icon: FileText },
];

const EMPTY = {
  // step 1
  full_name: "", dob: "", nationality: "", current_country: "", passport_country: "",
  marital_status: "", dependants: "", phone: "", email: "",
  // step 2
  highest_qualification: "", institution_names: "", graduation_year: "",
  academic_gaps: "", current_studies: "", previous_refusals: "",
  english_test: "", english_score: "", study_history: "",
  // step 3
  current_employment: "", previous_employment: "", position_title: "",
  salary: "", company_name: "", work_duration: "", career_progression: "",
  // step 4
  sponsor_name: "", sponsor_relationship: "", annual_income: "", bank_savings: "",
  loan_details: "", assets: "", funds_source: "", tuition_payer: "",
  // step 5
  intended_course: "", provider: "", why_australia: "", why_provider: "",
  why_course: "", similar_courses_home: "", future_career: "",
  // step 6
  gs_why_australia: "", gs_why_course: "", gs_course_relevance: "",
  gs_future_plans: "", gs_home_ties: "", gs_career_benefit: "",
};

const TextField = ({ label, name, value, onChange, type = "text", placeholder, hint, testid }) => (
  <label className="block">
    <span className="text-[11px] font-bold tracking-[0.16em] text-[#29324A] uppercase block mb-2">{label}</span>
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full border border-slate-200 px-3.5 py-3 text-sm focus:border-[#29324A] focus:outline-none transition-colors"
    />
    {hint && <span className="text-[11px] text-slate-400 mt-1 block">{hint}</span>}
  </label>
);

const TextArea = ({ label, name, value, onChange, placeholder, hint, rows = 4, max = 1200, testid }) => (
  <label className="block">
    <span className="text-[11px] font-bold tracking-[0.16em] text-[#29324A] uppercase block mb-2">{label}</span>
    <textarea
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      maxLength={max}
      data-testid={testid}
      className="w-full border border-slate-200 px-3.5 py-3 text-sm focus:border-[#29324A] focus:outline-none transition-colors"
    />
    <div className="flex justify-between mt-1">
      {hint ? <span className="text-[11px] text-slate-400">{hint}</span> : <span />}
      <span className="text-[11px] text-slate-400">{(value || "").length} / {max}</span>
    </div>
  </label>
);

function clientRiskCheck(d) {
  const risks = [];
  if (d.academic_gaps && /\b(2|3|4|5|6|7|8|9|10)\s*(year|yr)/i.test(d.academic_gaps)) risks.push("Academic gap may need a detailed explanation.");
  if (d.previous_refusals && /yes|refus/i.test(d.previous_refusals)) risks.push("Previous visa refusal — refusal history must be fully disclosed and explained.");
  if (d.intended_course && d.position_title && !commonOverlap(d.intended_course, d.position_title) && !commonOverlap(d.intended_course, d.highest_qualification || "")) {
    risks.push("Course may appear unrelated to previous studies/employment — strengthen the career-progression narrative.");
  }
  if (!d.bank_savings && !d.annual_income) risks.push("Financial profile is thin — add savings, sponsor income or loan details.");
  if (!d.future_career) risks.push("Add a clear post-study career plan to strengthen the genuine-temporary-stay narrative.");
  return risks;
}
const commonOverlap = (a, b) => {
  const A = (a || "").toLowerCase().split(/\W+/).filter((w) => w.length > 3);
  const B = (b || "").toLowerCase();
  return A.some((w) => B.includes(w));
};

export default function VisaStatementBuilder() {
  const [step, setStep] = useState(0); // 0 = intro
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [risks, setRisks] = useState([]);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setData({ ...EMPTY, ...JSON.parse(saved) });
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (e) { /* noop */ }
  }, [data]);

  useEffect(() => { setRisks(clientRiskCheck(data)); }, [data]);

  const update = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));

  const next = () => setStep((s) => Math.min(s + 1, STEP_DEFS.length));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!data.email || !data.full_name) {
      toast.error("Please provide your name and email so we can save your draft.");
      setStep(1);
      return;
    }
    if (!consent) {
      toast.error("Please accept the consent statement to continue.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post("/gs-builder/submit", {
        name: data.full_name,
        email: data.email,
        phone: data.phone,
        nationality: data.nationality,
        consent: true,
        consent_timestamp: new Date().toISOString(),
        profile: data,
      });
      const sid = res.data.id;
      setSubmissionId(sid);
      // Poll for result
      const deadline = Date.now() + 180000; // 3 minutes
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const r = await apiClient.get(`/gs-builder/result/${sid}`);
          if (r.data.status === "ready" && r.data.ai) {
            setResult(r.data.ai);
            setStep(STEP_DEFS.length + 1);
            toast.success("Your draft is ready. Review below and book a consultation to refine it with our team.");
            return;
          }
          if (r.data.status === "failed") {
            toast.error("Draft generation failed. Please try again or contact us.");
            return;
          }
        } catch (_e) { /* keep polling */ }
      }
      toast.error("Still generating — please refresh in a minute. Your draft is saved.");
    } catch (e) {
      toast.error("Could not start generation. Please try again or contact us.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    if (!window.confirm("Clear all saved progress?")) return;
    setData(EMPTY); setResult(null); setStep(0); setSubmissionId(null); localStorage.removeItem(STORAGE_KEY);
  };

  const emailDraft = async () => {
    if (!submissionId) return;
    setEmailing(true);
    try {
      await apiClient.post(`/gs-builder/email/${submissionId}`);
      toast.success(`Draft sent to ${data.email}. Please check your inbox.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send email. Please try again later.";
      toast.error(msg);
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="GS & SOP Statement Builder · Subclass 500"
        title="Build Your Genuine Student Statement Professionally."
        subtitle="Guided, professionally structured assistance to help prepare your Australian Student Visa profile, GS responses, SOP and supporting documents — aligned with current Department of Home Affairs Genuine Student guidance."
        image="/assets/graduate.jpg"
      />

      {/* Intro */}
      {step === 0 && (
        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-px bg-slate-200 mb-10">
              {[
                { icon: Sparkles, title: "Guided drafting", desc: "Automated GS & SOP drafts tailored to your profile, then refined by our specialists." },
                { icon: ShieldCheck, title: "Risk alerts", desc: "We flag study gaps, course-career mismatches and weak finances." },
                { icon: FileText, title: "Reviewed by an RMA", desc: "Drafts are reviewable by our Registered Migration Agents before lodgement." },
              ].map((c, i) => (
                <div key={i} className="bg-white p-6">
                  <c.icon className="h-7 w-7 text-[#C43A89] mb-3" />
                  <h3 className="font-display text-lg text-[#29324A] mb-2">{c.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center">
              <button
                type="button"
                data-testid="gs-start-btn"
                onClick={() => setStep(1)}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C43A89] to-[#a22b6e] hover:from-[#a22b6e] hover:to-[#7a1f51] text-white font-semibold py-4 px-8 text-sm tracking-wider uppercase btn-shimmer shadow-md shadow-[#C43A89]/20"
              >
                Start Assessment <ChevronRight className="h-4 w-4" />
              </button>
              <p className="text-xs text-slate-400 mt-3">Your progress is auto-saved in this browser.</p>
            </div>
          </div>
        </section>
      )}

      {/* Step indicator + form */}
      {step >= 1 && step <= STEP_DEFS.length && (
        <section data-testid="gs-form-section" className="py-10 md:py-16 bg-[#F8F9FA]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Stepper */}
            <div className="mb-8 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                {STEP_DEFS.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-2">
                    <div
                      className={`flex items-center gap-2 px-3 py-2 text-xs font-bold tracking-wider uppercase transition-all ${
                        step === s.id
                          ? "bg-[#29324A] text-white"
                          : step > s.id
                          ? "bg-white text-[#29324A] border border-[#29324A]"
                          : "bg-white text-slate-400 border border-slate-200"
                      }`}
                    >
                      <s.icon className="h-3.5 w-3.5" /> {s.id}. {s.label}
                    </div>
                    {i < STEP_DEFS.length - 1 && <span className="text-slate-300">·</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-8">
              {/* Form */}
              <div className="lg:col-span-8 bg-white p-6 md:p-8 border border-slate-200">
                {step === 1 && (
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="Full Name" name="full_name" value={data.full_name} onChange={update} testid="gs-full-name" />
                    <TextField label="Date of Birth" name="dob" value={data.dob} onChange={update} type="date" testid="gs-dob" />
                    <TextField label="Nationality" name="nationality" value={data.nationality} onChange={update} testid="gs-nationality" />
                    <TextField label="Current Country" name="current_country" value={data.current_country} onChange={update} />
                    <TextField label="Passport Country" name="passport_country" value={data.passport_country} onChange={update} />
                    <TextField label="Marital Status" name="marital_status" value={data.marital_status} onChange={update} placeholder="Single / Married / De facto" />
                    <TextField label="Number of Dependants" name="dependants" value={data.dependants} onChange={update} type="number" />
                    <TextField label="Phone" name="phone" value={data.phone} onChange={update} />
                    <div className="sm:col-span-2">
                      <TextField label="Email Address" name="email" value={data.email} onChange={update} type="email" testid="gs-email" hint="Used only to save your draft and contact you." />
                    </div>
                  </div>
                )}
                {step === 2 && (
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="Highest Qualification" name="highest_qualification" value={data.highest_qualification} onChange={update} />
                    <TextField label="Institution Name(s)" name="institution_names" value={data.institution_names} onChange={update} />
                    <TextField label="Graduation Year" name="graduation_year" value={data.graduation_year} onChange={update} />
                    <TextField label="English Test" name="english_test" value={data.english_test} onChange={update} placeholder="IELTS / PTE / TOEFL" />
                    <TextField label="English Test Score" name="english_score" value={data.english_score} onChange={update} placeholder="e.g. IELTS 6.5 (no band < 6.0)" />
                    <TextField label="Current Studies" name="current_studies" value={data.current_studies} onChange={update} />
                    <div className="sm:col-span-2">
                      <TextArea label="Academic Gaps (years and reason)" name="academic_gaps" value={data.academic_gaps} onChange={update} rows={3} />
                    </div>
                    <div className="sm:col-span-2">
                      <TextArea label="Previous Visa Refusals (any country)" name="previous_refusals" value={data.previous_refusals} onChange={update} rows={3} />
                    </div>
                    <div className="sm:col-span-2">
                      <TextArea label="Study History Timeline" name="study_history" value={data.study_history} onChange={update} rows={4} hint="List qualifications chronologically with dates." />
                    </div>
                  </div>
                )}
                {step === 3 && (
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="Current Employment" name="current_employment" value={data.current_employment} onChange={update} />
                    <TextField label="Position Title" name="position_title" value={data.position_title} onChange={update} />
                    <TextField label="Company Name" name="company_name" value={data.company_name} onChange={update} />
                    <TextField label="Annual Salary" name="salary" value={data.salary} onChange={update} />
                    <TextField label="Work Duration" name="work_duration" value={data.work_duration} onChange={update} placeholder="e.g. 3 years" />
                    <div className="sm:col-span-2">
                      <TextArea label="Previous Employment" name="previous_employment" value={data.previous_employment} onChange={update} rows={4} />
                    </div>
                    <div className="sm:col-span-2">
                      <TextArea label="Career Progression Summary" name="career_progression" value={data.career_progression} onChange={update} rows={4} />
                    </div>
                  </div>
                )}
                {step === 4 && (
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="Sponsor Name" name="sponsor_name" value={data.sponsor_name} onChange={update} />
                    <TextField label="Relationship with Sponsor" name="sponsor_relationship" value={data.sponsor_relationship} onChange={update} />
                    <TextField label="Sponsor Annual Income" name="annual_income" value={data.annual_income} onChange={update} />
                    <TextField label="Bank Savings (AUD equivalent)" name="bank_savings" value={data.bank_savings} onChange={update} />
                    <TextField label="Loan Details" name="loan_details" value={data.loan_details} onChange={update} />
                    <TextField label="Property / Assets" name="assets" value={data.assets} onChange={update} />
                    <TextField label="Source of Funds" name="funds_source" value={data.funds_source} onChange={update} />
                    <TextField label="Who Will Pay Tuition?" name="tuition_payer" value={data.tuition_payer} onChange={update} />
                  </div>
                )}
                {step === 5 && (
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="Intended Course" name="intended_course" value={data.intended_course} onChange={update} />
                    <TextField label="University / Provider" name="provider" value={data.provider} onChange={update} />
                    <div className="sm:col-span-2"><TextArea label="Why Australia?" name="why_australia" value={data.why_australia} onChange={update} /></div>
                    <div className="sm:col-span-2"><TextArea label="Why this Provider?" name="why_provider" value={data.why_provider} onChange={update} /></div>
                    <div className="sm:col-span-2"><TextArea label="Why this Course?" name="why_course" value={data.why_course} onChange={update} /></div>
                    <div className="sm:col-span-2"><TextArea label="Similar Courses Available in Your Home Country" name="similar_courses_home" value={data.similar_courses_home} onChange={update} hint="Be honest. Acknowledging local options and then explaining your reason strengthens your application." /></div>
                    <div className="sm:col-span-2"><TextArea label="Career Outcome After Graduation" name="future_career" value={data.future_career} onChange={update} /></div>
                  </div>
                )}
                {step === 6 && (
                  <div className="space-y-5">
                    <TextArea label="Q1. Why do you want to study in Australia?" name="gs_why_australia" value={data.gs_why_australia} onChange={update} rows={4} max={1500} hint="Mention quality, accreditation, English-medium, return-on-investment for your home country career." />
                    <TextArea label="Q2. Why did you choose this course?" name="gs_why_course" value={data.gs_why_course} onChange={update} rows={4} max={1500} />
                    <TextArea label="Q3. How does this course relate to your previous studies and employment?" name="gs_course_relevance" value={data.gs_course_relevance} onChange={update} rows={4} max={1500} />
                    <TextArea label="Q4. What are your post-study plans?" name="gs_future_plans" value={data.gs_future_plans} onChange={update} rows={4} max={1500} />
                    <TextArea label="Q5. What ties keep you connected to your home country?" name="gs_home_ties" value={data.gs_home_ties} onChange={update} rows={4} max={1500} hint="Family, property, employment, business, future role." />
                    <TextArea label="Q6. Why is this course beneficial for your career?" name="gs_career_benefit" value={data.gs_career_benefit} onChange={update} rows={4} max={1500} />
                  </div>
                )}
                {step === 7 && (
                  <div>
                    <h3 className="font-display text-2xl text-[#29324A] mb-3">Review &amp; Generate Draft</h3>
                    <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                      We'll send your profile to Claude Sonnet 4.5 to generate a draft GS response set, SOP and supporting letter. This is an aid only — your final statements must be reviewed by your Registered Migration Agent.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm text-slate-700 mb-6">
                      {Object.entries({
                        Name: data.full_name, Email: data.email, Nationality: data.nationality,
                        Course: data.intended_course, Provider: data.provider,
                      }).map(([k, v]) => (
                        <div key={k} className="bg-[#F8F4FB] p-3 border-l-2 border-[#29324A]">
                          <div className="text-[10px] font-bold tracking-widest uppercase text-[#29324A]">{k}</div>
                          <div className="mt-1 text-slate-700">{v || <em className="text-slate-400">not provided</em>}</div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={submit}
                      disabled={loading || !consent}
                      data-testid="gs-generate-btn"
                      className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C43A89] to-[#a22b6e] hover:from-[#a22b6e] hover:to-[#7a1f51] disabled:opacity-60 text-white font-semibold py-3.5 px-7 text-sm tracking-wider uppercase btn-shimmer shadow-md shadow-[#C43A89]/20"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      {loading ? "Generating your draft…" : "Generate Draft"}
                    </button>
                    <button type="button" onClick={reset} className="ml-3 text-xs text-slate-500 hover:text-[#C43A89] uppercase tracking-wider">Clear progress</button>
                    <ConsentCheckbox value={consent} onChange={setConsent} testid="gs-consent" />
                  </div>
                )}

                {/* Step nav */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={prev}
                    disabled={step === 1}
                    data-testid="gs-prev-btn"
                    className="inline-flex items-center gap-1 text-xs font-bold tracking-wider uppercase text-slate-600 hover:text-[#29324A] disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> Back
                  </button>
                  {step < STEP_DEFS.length && (
                    <button
                      type="button"
                      onClick={next}
                      data-testid="gs-next-btn"
                      className="inline-flex items-center gap-1 bg-[#29324A] hover:bg-[#1f2638] text-white text-xs font-bold tracking-wider uppercase py-2.5 px-5 transition-colors"
                    >
                      Next <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-4 space-y-4">
                <div className="bg-white border border-slate-200 p-5">
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5" /> Live Risk Alerts
                  </div>
                  {risks.length === 0 ? (
                    <p className="text-sm text-slate-500 flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" /> No obvious risks detected so far.</p>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {risks.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-700">
                          <AlertTriangle className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="bg-[#F8F4FB] border border-slate-200 p-5">
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#29324A] uppercase mb-2 flex items-center gap-2">
                    <ShieldCheck className="h-3.5 w-3.5" /> Privacy
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Your responses are saved only in your browser until you generate a draft. After generation, the draft is sent to our team so a Registered Migration Agent can follow up if you ask us to.
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </section>
      )}

      {/* Result */}
      {step === STEP_DEFS.length + 1 && result && (
        <section data-testid="gs-result-section" className="py-12 md:py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            <div>
              <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">Your Draft</div>
              <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight">
                Draft generated. Now refine it with our team.
              </h2>
              <p className="mt-3 text-sm text-slate-600">
                This is a draft only. A Registered Migration Agent should review and adapt every paragraph to your specific facts before lodgement.
              </p>
            </div>

            {result.profile_summary && (
              <div className="bg-[#F8F9FA] border-l-4 border-[#29324A] p-6">
                <h3 className="font-display text-xl text-[#29324A] mb-2">Migration Agent Style — Profile Summary</h3>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{result.profile_summary}</p>
              </div>
            )}

            {Array.isArray(result.gs_responses) && (
              <div className="space-y-4">
                <h3 className="font-display text-2xl text-[#29324A]">Genuine Student (GS) Responses</h3>
                {result.gs_responses.map((q, i) => (
                  <div key={i} className="bg-white border border-slate-200 p-5">
                    <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">Q{i + 1}</div>
                    <h4 className="font-display text-lg text-[#29324A] mb-2">{q.question}</h4>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{q.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {result.sop_draft && (
              <div className="border border-slate-200 p-6 md:p-8">
                <h3 className="font-display text-2xl text-[#29324A] mb-3">Statement of Purpose — Draft</h3>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{result.sop_draft}</p>
              </div>
            )}

            {result.supporting_letter && (
              <div className="border border-slate-200 p-6 md:p-8 bg-[#F8F4FB]">
                <h3 className="font-display text-2xl text-[#29324A] mb-3">Supporting Explanation Letter — Draft</h3>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{result.supporting_letter}</p>
              </div>
            )}

            {Array.isArray(result.risk_alerts) && result.risk_alerts.length > 0 && (
              <div className="bg-white border-l-4 border-[#C43A89] p-6">
                <h3 className="font-display text-xl text-[#29324A] mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-[#C43A89]" /> Risk Alerts
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  {result.risk_alerts.map((r, i) => (<li key={i}>· {r}</li>))}
                </ul>
              </div>
            )}

            {Array.isArray(result.recommendations) && result.recommendations.length > 0 && (
              <div className="bg-[#F8F9FA] p-6 border border-slate-200">
                <h3 className="font-display text-xl text-[#29324A] mb-3">Recommendations</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  {result.recommendations.map((r, i) => (<li key={i}>· {r}</li>))}
                </ul>
              </div>
            )}

            <div className="bg-[#29324A] grain text-white p-7">
              <h3 className="font-display text-2xl mb-2">Get this draft reviewed by a Registered Migration Agent.</h3>
              <p className="text-sm text-slate-300 mb-5">Our migration team can adapt these drafts to your facts, lodge your application and represent you through to a decision.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/book-consultation"
                  data-testid="gs-book-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white font-semibold py-3 px-7 text-xs uppercase tracking-wider btn-shimmer"
                >
                  Book a Consultation <ChevronRight className="h-3.5 w-3.5" />
                </Link>
                <button
                  type="button"
                  onClick={emailDraft}
                  disabled={emailing || !submissionId}
                  data-testid="gs-email-draft"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white/10 font-semibold py-3 px-7 text-xs uppercase tracking-wider disabled:opacity-50 transition-colors"
                >
                  {emailing ? (<Loader2 className="h-3.5 w-3.5 animate-spin" />) : (<Mail className="h-3.5 w-3.5" />)}
                  {emailing ? "Sending..." : "Email me my draft"}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#F8F9FA] py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Disclaimer>
            This tool provides general guidance only and does not guarantee visa approval. All applications are assessed individually by the Department of Home Affairs under current migration law and policy. Every draft must be reviewed by a Registered Migration Agent before lodgement.
          </Disclaimer>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
